<template>
  <v-card-text>
    <v-toolbar flat color="white" class="px-0 pb-2 pt-0">
      <v-autocomplete v-model="timezone"
                      @change="$emit('update:timezone', $event)"
                      label="Timezone"
                      item-value="index"
                      item-text="label"
                      :items="timezones"
                      class="ml-4"
                      width="200px"
                      hide-details />
      <v-spacer />
      <v-progress-linear
        :style="{ opacity: loading ? 1 : 0 }"
        style="width:200px"
        :indeterminate="loading"
        color="primary"
        class="mr-2" />
      <v-spacer />
      <v-btn color="primary" fab text outlined small @click="$refs[calendarRef].prev()">
        <v-icon small>arrow_back_ios</v-icon>
      </v-btn>
      <v-btn color="primary" fab text outlined small @click="$refs[calendarRef].next()">
        <v-icon small>arrow_forward_ios</v-icon>
      </v-btn>
      <v-toolbar-title>
        <a @click="focusOn(timezonedNow)">{{ timezonedNowForHumans }}</a>
      </v-toolbar-title>
    </v-toolbar>
    <slot name="options"/>
    <v-sheet :height="$attrs.height ? $attrs.height : '380px'">
      <v-calendar :ref="calendarRef"
                  v-model="start"
                  type="week"
                  :start="start"
                  :now="timezonedNowForInputs">
        <template v-slot:day-body="{ date, timeToY, minutesToPixels }">
          <template v-if="showGroups && (group !== false)">
            <template v-for="slot in timezonedSlots[date]">
              <div :key="slot.title"
                  :style="{ top: (timeToY(slot.time)+5) + 'px', height: minutesToPixels(48) + 'px' }"
                  class="v-calendar__event with-global-time"
                  :class="slotClasses(slot, 'success', true)"
                  :data-test="`${date} ${slot.time}` | timezone(selectedTimezone.name) | toISOString">
                <v-icon color="white">language</v-icon>
              </div>
            </template>
            <template v-for="slot in timezonedSlots[date]">
              <div :key="slot.title"
                  :style="{ top: (timeToY(slot.time)+5) + 'px', height: minutesToPixels(48) + 'px', right: '4px', left: '40px' }"
                  class="v-calendar__event with-time"
                  :class="slotClasses(slot, 'primary')"
                  @click="select(slot)"
                  :data-test="`${date} ${slot.time}` | timezone(selectedTimezone.name) | toISOString">
                {{ groupCustomSlots(date, slot) }}
              </div>
            </template>
          </template>
          <template v-else>
            <template v-for="slot in timezonedSlots[date]">
              <div :key="slot.title"
                  :style="{ top: (timeToY(slot.time)+5) + 'px', height: minutesToPixels(48) + 'px', right: (showGroups && timezonedGroupsSlots.hasOwnProperty(date) && timezonedGroupsSlots[date].hasOwnProperty(slot.time) ? '36' : '4') + 'px' }"
                  class="v-calendar__event with-time"
                  :class="slotClasses(slot)"
                  @click="select(slot)"
                  :data-test="`${date} ${slot.time}` | timezone(selectedTimezone.name) | toISOString"
                  :data-test-available-slots="slot.available > 0">
                {{ slot.available }}/{{ slot.total }}
              </div>
            </template>
            <template v-if="showGroups">
              <template v-for="(groups, time) in timezonedGroupsSlots[date]">
                <div :key="`groups-${time}`"
                    :style="{ top: (timeToY(time)+5) + 'px', height: minutesToPixels(48) + 'px' }"
                    class="v-calendar__event with-group-time primary text-sm-center"
                    @click="showGroupsDetails(date, time)"
                    :data-test="`${date} ${time}` | timezone(selectedTimezone.name) | toISOString">
                  {{ Object.keys(groups).length }}
                </div>
              </template>
            </template>
          </template>
        </template>
      </v-calendar>
    </v-sheet>
    <v-dialog v-if="groupsDetails.date && groupsDetails.time"
              v-model="groupsDetails.show"
              width="500">
      <v-card>
        <v-card-title class="headline grey lighten-2"
                      primary-title>
          {{ groupsDetails.date }} {{ groupsDetails.time }} Groups
        </v-card-title>

        <v-card-text>
          <v-list>
            <v-list-item v-for="(slot, group) in timezonedGroupsSlots[groupsDetails.date][groupsDetails.time]" :key="`group-details-${group}`">
              <v-list-item-action style="min-width: 28px">
                <v-icon color="gray lighten-1" small>lens</v-icon>
              </v-list-item-action>

              <v-list-item-content>
                <v-list-item-title v-text="group" class="font-weight-bold"></v-list-item-title>
                <v-list-item-subtitle v-html="`${slot.available}/${slot.total}`"></v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary"
                 flat
                 @click="groupsDetails.show = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card-text>
</template>

<script>
import moment from 'moment'
import { Bus } from '@/bus'
import ModelBinding from '@/mixins/ModelBinding'
import { queryAvailableSlots } from '@/services/Schedule'
import RequestCancelToken from '@/mixins/RequestCancelToken'
import { timezones, getDefaultTimezoneIndex, getTimezoneIndex, datetimeForHumans, dateForInputs } from '@/services/time'

export default {
  name: 'SlotsCalendar',
  mixins: [ ModelBinding, RequestCancelToken ],
  props: {
    /**
       * Number of required time slots
       */
    requiredSlotsCount: {
      type: Number,
      required: false,
      default: 1
    },
    /**
       * Location to search for the timezone
       */
    location: {
      type: String,
      required: false
    },
    /**
     *
     */
    show: {
      type: Boolean,
      default: true
    },
    /**
     *
     */
    range: {
      type: Boolean,
      default: false
    },
    /**
     * Initial timezone index
     */
    initialTimezone: {
      type: Number,
      default: getDefaultTimezoneIndex()
    },
    /**
     *  Initial date to focus on when the calendar is created
     */
    initialDate: {
      type: Object,
      default: () => ({})
    },
    /**
     * Force slot selection even if no slots available
     */
    selectingEmptySlots: {
      type: Boolean,
      default: false
    },
    /**
     * Show groups details icon
     */
    showGroups: {
      type: Boolean,
      default: false
    },
    /**
     * Show group slots
     */
    group: {
      default: false
    },
    /**
     * Disable slots selection
     */
    disableSelection: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      loading: false,
      slots: [],
      now: moment(),
      // Start of week
      start: null,
      timezone: this.initialTimezone,
      timeout: null,
      timezones,
      selectedRange: [],
      groupsDetails: {
        show: false,
        date: null,
        time: null
      }
    }
  },
  mounted () {
    this.timezone = this.initialTimezone

    if (this.show) {
      this.queryAvailableSlots().then(() => {
        const date = this.initialDate instanceof moment
          ? this.initialDate
          : this.timezonedNow
        this.focusOn(date)
      })
    }
  },
  computed: {
    /**
       * Calendar component ref
       */
    calendarRef () {
      return `calendar-${this._uid}`
    },
    /**
       * Input field text
       */
    fieldText () {
      return (this.model === null)
        ? ''
        : `Current login window ${this.timezonedSlotForHumans} - ${this.selectedTimezone.label}`
    },
    /**
       * The selected timezone to query its available time slots
       */
    selectedTimezone () {
      return this.timezones[this.timezone]
    },
    /**
       * Now at the selected timezone
       */
    timezonedNow () {
      return moment.tz(this.now, this.selectedTimezone.name)
    },
    /**
       * Map slots according to selected timezone
       */
    timezonedSlots () {
      let slots = {}

      this.slots.forEach(slot => {
        const timezonedSlot = moment.utc(slot.date).tz(this.selectedTimezone.name)

        if (this.timezonedNow.isAfter(timezonedSlot) || slot.assessment_group !== '') {
          return
        }

        const timezonedDate = timezonedSlot.format(dateForInputs)

        slots[timezonedDate] = (slots[timezonedDate] || [])

        slots[timezonedDate]
          .push({
            utc: slot.date,
            time: timezonedSlot.format('HH:mm'),
            available: slot.available_slots,
            total: slot.total_slots
          })
      })

      return slots
    },
    /**
     * Map groups' slots according to selected timezone
     */
    timezonedGroupsSlots () {
      let slots = {}

      this.slots.forEach(slot => {
        const timezonedSlot = moment.utc(slot.date).tz(this.selectedTimezone.name)

        if (this.timezonedNow.isAfter(timezonedSlot) || slot.assessment_group === '') {
          return
        }

        const timezonedDate = timezonedSlot.format(dateForInputs)

        slots[timezonedDate] = (slots[timezonedDate] || {})
        slots[timezonedDate][timezonedSlot.format('HH:mm')] = (slots[timezonedDate][timezonedSlot.format('HH:mm')] || {})

        slots[timezonedDate][timezonedSlot.format('HH:mm')][slot.assessment_group] = {
          available: slot.available_slots,
          total: slot.total_slots
        }
      })
      return slots
    },
    /**
       * Now at selected timezone formatted for users
       */
    timezonedNowForHumans () {
      return this.timezonedNow.format(datetimeForHumans)
    },
    /**
       * Now at selected timezone formatted for inputs
       */
    timezonedNowForInputs () {
      return this.timezonedNow.format(dateForInputs)
    },
    /**
       * Display due date for users
       */
    timezonedSlotForHumans () {
      return (this.model)
        ? moment.utc(this.model).tz(this.selectedTimezone.name).format(datetimeForHumans)
        : '-'
    },
    /**
       * Available slots query parameters
       */
    query () {
      const nowInUtc = this.timezonedNow
        .clone()
        .add(1, 'hour')
        .utc()

      const startOfWeekInUtc = moment.tz(this.start, this.selectedTimezone.name)
        .startOf('week')
        .utc()

      const endOfWeekInUtc = moment.tz(this.start, this.selectedTimezone.name)
        .endOf('week')
        .add(1, 'hour')
        .utc()

      return {
        token: null,
        fromDate: startOfWeekInUtc.isBefore(nowInUtc) ? nowInUtc : startOfWeekInUtc,
        toDate: endOfWeekInUtc
      }
    },
    /**
       * To show clear icon if an assessment date has been selected
       */
    clearIcon () {
      return this.model === null
        ? null
        : 'close'
    }
  },
  watch: {
    /**
       * Query available slots if start of week changed
       */
    start: 'queryAvailableSlots',
    /**
       * Update week start when changing timezone
       */
    timezone: {
      handler () {
        this.start = this.timezonedNow
          .clone()
          .startOf('week')
          .format(dateForInputs)

        if (this.show) {
          this.queryAvailableSlots().then(() => {
            this.focusOn(this.model ? moment(this.model).tz(this.selectedTimezone.name) : this.timezonedNow)
          })
        }
      },
      immediate: true
    },
    /**
       * Update timezone when providing a location
       */
    location: {
      handler () {
        const location = (this.location || '').replace(' ', '_')

        this.timezone = (this.timezone === null)
          ? getTimezoneIndex(location, false) || getDefaultTimezoneIndex()
          : getTimezoneIndex(location, false) || this.timezone
      },
      immediate: true
    },
    /**
       * Show calendar dialog
       */
    show: {
      handler () {
        if (this.show) {
          this.startTimer()

          this.queryAvailableSlots().then(() => {
            this.focusOn(this.model ? moment(this.model).tz(this.selectedTimezone.name) : this.timezonedNow)
          })
        } else {
          this.clearTimer()
        }
      }
    }
  },
  methods: {
    /**
     * Focus calendar on a particular time
     */
    focusOn (date) {
      // Go to the week
      const weekStartDate = date.clone().add(-date.day(), 'day').startOf('day')

      if (!weekStartDate.isSame(this.start)) {
        this.start = weekStartDate.format(dateForInputs)
      }

      // Scroll to the hour
      const hour = date.hour() > 3 ? date.clone().subtract(3, 'hour') : date.clone().startOf('day')
      this.$refs[this.calendarRef].scrollToTime(hour.format('HH:mm'))
    },
    /**
       * Update now time
       */
    updateNow () {
      this.$set(this, 'now', this.now.clone().endOf('minute').add(1, 'second'))
    },
    /**
     * Slot classes
     */
    slotClasses (slot, success = 'success', ignoreSelecting = false) {
      const slotInRange = this.range &&
        (moment(slot.utc).isBetween(moment(this.selectedRange[0]), moment(this.selectedRange[1])) ||
        this.selectedRange.includes(slot.utc))

      const slotSelected = slot.utc === this.model || slotInRange

      return [
        (slot.available < this.requiredSlotsCount) ? 'red' : success,
        slotSelected && !ignoreSelecting ? 'darken-1' : 'lighten-1'
      ]
    },
    /**
       * Start timer to update now at the start of every minute
       */
    startTimer () {
      this.$set(this, 'now', moment())
      const remainingSeconds = 60 - this.now.second()

      this.timeout = setTimeout(() => {
        this.updateNow()

        this.timeout = setInterval(() => {
          this.updateNow()
        }, 1000 * 60)
      }, 1000 * remainingSeconds)
    },
    /**
       * Clear timer update
       */
    clearTimer () {
      clearTimeout(this.timeout)
      clearInterval(this.timeout)
    },
    /**
       * Get a list of available slots
       */
    queryAvailableSlots () {
      if (!this.show) {
        return
      }

      this.$set(this, 'slots', [])
      this.$set(this, 'loading', true)
      this.requestCancelTokenChecker()

      return queryAvailableSlots(this.query, this.requestToken.token)
        .then(({ data: { schedule } }) => {
          this.$set(this, 'loading', false)
          this.requestCancelTokenClearer()
          this.$set(this, 'slots', schedule)
        })
        .catch((error) => {
          if (error.message !== this.requestCancelMessage) {
            this.$set(this, 'loading', false)
            this.requestCancelTokenClearer()
            Bus.$emit('alert', { color: 'error', message: error })
          }
        })
    },
    /**
       * Select clicked time slot
       */
    select (slot) {
      if (this.disableSelection) {
        return
      }
      // Do we need to unselect the calendar slot?
      const reset = !this.range
        ? slot.available < this.requiredSlotsCount || this.model === slot.utc
        : this.selectedRange.length >= 2 || this.selectedRange[0] === slot.utc

      if (reset && !this.selectingEmptySlots) {
        this.model = null
        this.selectedRange = []
        return
      }

      // If the range option is enabled, push to selected range
      if (this.range) {
        if (moment(this.selectedRange[0]).isAfter(moment(slot.utc))) {
          this.selectedRange.unshift(slot.utc)
        } else {
          this.selectedRange.push(slot.utc)
        }
        this.model = this.selectedRange
      } else {
        this.model = slot.utc
      }
    },
    /**
     * Show time slot groups' details
     */
    showGroupsDetails (date, time) {
      this.groupsDetails.date = date
      this.groupsDetails.time = time
      this.groupsDetails.show = true
    },
    /**
     * Get group slots
     */
    groupCustomSlots (date, slot) {
      return (this.timezonedGroupsSlots[date] && this.timezonedGroupsSlots[date][slot.time] && this.timezonedGroupsSlots[date][slot.time][this.group])
        ? `${this.timezonedGroupsSlots[date][slot.time][this.group].available}/${this.timezonedGroupsSlots[date][slot.time][this.group].total}`
        : `${slot.available}/${slot.total}`
    },
    /**
     * Update timezone index
     *
     * @param {Number} timezoneIndex
     */
    setTimezoneIndex (timezoneIndex) {
      this.timezone = timezoneIndex
    }
  }
}
</script>

<style lang="sass">
.v-calendar-daily_head-day-label:hover
  cursor: default !important
  text-decoration: none !important

.v-calendar-daily_head-day
  pointer-events: none

.v-calendar-daily__scroll-area
  overflow-y: scroll

.v-calendar__event
  overflow: hidden
  text-overflow: ellipsis
  white-space: nowrap
  border-radius: 2px
  background-color: #1867c0
  color: #ffffff
  border: 1px solid #1867c0
  font-size: 14px
  padding: 6px
  cursor: pointer
  margin-bottom: 1px
  left: 4px
  margin-right: 8px
  position: relative

  &.with-time
    position: absolute
    margin-right: 0

  &.with-global-time
    position: absolute
    right: 145px
    left: 4px
    margin-right: 0
    padding: 3px

  &.with-group-time
    position: absolute
    right: 4px
    left: 150px
    margin-right: 0
</style>
